import { fmtMoney, fmtMonthShort } from "../../lib/format";
import MoneyCell from "./MoneyCell";
import RatioCell from "./RatioCell";
import DragScrollContainer from "../DragScrollContainer";
import { currentMonthKey } from "../../lib/dateWindow";
import { toDisplay, toStored } from "../../lib/subconsultants";
import { Plus, Trash2, Rows3 } from "lucide-react";

// SUB-CONSULTANTS — their own section below the forecast. Costs are typed as
// POSITIVE figures here and stored negative (existing data convention), so the
// total below is a deduction from the gross forecast.
export function SubConsultantsGrid({ months, scopes, contractValues = ["Yes", "Tentative"], onChange, onAdd, onRemove, canEditPast = false }) {
  const W_LABEL = 190, W_NOTE = 150, W_STATUS = 108;
  const CUR = currentMonthKey();
  const isPast = (mo) => mo < CUR;
  const lockPast = (mo) => isPast(mo) && !canEditPast;

  const idxs = (scopes || []).map((_, i) => i).filter((i) => scopes[i].isSubConsultant);

  const update = (idx, patch) =>
    onChange(scopes.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  // Ratio entry, same as the forecast scope grid: RatioCell hands back an
  // already-signed dollar figure plus the typed ratio label, so store both
  // as-is rather than running them back through toStored().
  const updateMonthRatio = (idx, mo, dollars, ratioStr) =>
    onChange(scopes.map((s, i) => {
      if (i !== idx) return s;
      const revenueRatios = { ...(s.revenueRatios || {}) };
      if (ratioStr) revenueRatios[mo] = ratioStr;
      else delete revenueRatios[mo];
      return { ...s, revenue: { ...(s.revenue || {}), [mo]: dollars }, revenueRatios };
    }));

  // Allocation indicator — how much of the fee has been spread across months.
  const allocDecimal = (s) => {
    const fee = Math.abs(Number(s.contractValue) || 0);
    if (!fee) return null;
    const spreadTotal = months.reduce((a, mo) => a + toDisplay(s.revenue?.[mo]), 0);
    return spreadTotal ? spreadTotal / fee : null;
  };
  const allocColour = (dec) => {
    if (dec == null) return "text-[var(--rule-strong)]";
    if (dec > 1.005) return "text-[#6B2F2A]";
    if (dec < 0.995) return "text-[#8A6A1F]";
    return "text-[#2C4A38]";
  };
  const allocLabel = (dec) => (dec == null ? "—" : `${Math.round(dec * 1000) / 1000}`);

  // Spread the row's fee evenly across the editable months in view, so the
  // sub-consultant series has a real monthly shape (rounded to whole dollars,
  // remainder onto the last month).
  const spread = (idx) => {
    const s = scopes[idx];
    const fee = Math.abs(Number(s.contractValue) || 0);
    const targets = months.filter((mo) => !lockPast(mo));
    if (!fee || !targets.length) return;
    const per = Math.round(fee / targets.length);
    const revenue = { ...(s.revenue || {}) };
    months.forEach((mo) => { if (!lockPast(mo)) revenue[mo] = 0; });
    targets.forEach((mo, i) => {
      const amount = i === targets.length - 1 ? fee - per * (targets.length - 1) : per;
      revenue[mo] = toStored(amount);
    });
    update(idx, { revenue });
  };

  const rowMag = (s) => months.reduce((a, mo) => a + toDisplay(s.revenue?.[mo]), 0);  const monthMag = (mo) => idxs.reduce((a, i) => a + toDisplay(scopes[i].revenue?.[mo]), 0);
  const grand = months.reduce((a, mo) => a + monthMag(mo), 0);
  const cvTotal = idxs.reduce((a, i) => a + Math.abs(Number(scopes[i].contractValue) || 0), 0);

  if (!idxs.length) {
    return (
      <div className="border border-dashed border-[var(--rule-strong)] p-5 flex items-center justify-between gap-4 flex-wrap"
        data-testid="sub-consultants-empty">
        <div className="t-caption normal-case tracking-[0.06em] max-w-xl">
          No sub-consultants on this project. Add a row to record their fee — it is deducted
          from the forecast total to give the net fee the firm keeps.
        </div>
        <button onClick={onAdd}
          className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
          data-testid="add-subconsultants">
          <Plus size={11} /> Add sub consultant
        </button>
      </div>
    );
  }

  return (
    <div className="border border-[var(--rule)]" data-testid="sub-consultants-grid">
      <DragScrollContainer className="overflow-x-auto thin-scroll">
        <table className="text-xs border-collapse" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th className="sticky left-0 z-20 bg-[#F6F4EE] eyebrow text-left px-3 py-2 border-b border-r border-[var(--rule)]" style={{ width: W_LABEL, minWidth: W_LABEL }}>
                <div className="text-[#2C4A38]">Sub-consultants</div>
                <div className="normal-case tracking-[0.06em] text-[10px] text-[var(--soft-fg)]">deducted from the total</div>
              </th>
              <th className="px-2 py-2 eyebrow text-right border-b border-[var(--rule)] bg-[#F6F4EE]" style={{ minWidth: 108 }}>Fee</th>
              <th className="px-2 py-2 eyebrow text-left border-b border-[var(--rule)] bg-[#F6F4EE]" style={{ minWidth: W_STATUS }}>Status</th>
              <th className="px-2 py-2 eyebrow text-left border-b border-[var(--rule)]" style={{ minWidth: W_NOTE }}>Who / discipline</th>
              {months.map((mo) => (
                <th key={mo} className={`px-2 py-2 eyebrow text-right border-b border-[var(--rule)] ${isPast(mo) ? "bg-[var(--paper-soft)]/70 text-[var(--muted-fg)]" : "text-[var(--soft-fg)]"}`} style={{ minWidth: 88 }}>
                  {fmtMonthShort(mo)}
                </th>
              ))}
              <th className="px-3 py-2 eyebrow text-right border-b border-l border-[var(--rule-strong)] bg-[var(--paper-soft)] sticky right-0 z-20" style={{ minWidth: 130, boxShadow: "-1px 0 0 var(--rule-strong)" }}>Row total · spread</th>
            </tr>
          </thead>
          <tbody>
            {idxs.map((i) => {
              const s = scopes[i];
              return (
                <tr key={s.id || i} className="border-b border-[var(--rule)] bg-[#F6F4EE]" data-testid="sub-consultants-row">
                  <td className="sticky left-0 z-10 bg-[#F6F4EE] px-3 py-2 border-r border-[var(--rule)]" style={{ width: W_LABEL, minWidth: W_LABEL }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-display tracked-tight uppercase text-[var(--muted-fg)]">Sub consultant</span>
                      <div className="flex items-center">
                        <button onClick={() => spread(i)}
                          className="px-1.5 py-1 text-[var(--soft-fg)] hover:text-[var(--ink)] hover:bg-white"
                          title="Spread the fee evenly across the editable months in view"
                          data-testid="sub-spread">
                          <Rows3 size={12} />
                        </button>
                        <button onClick={() => onRemove(i)} className="p-1.5 text-[var(--soft-fg)] hover:text-[#6B2F2A]" title="Remove sub-consultant row" data-testid="sub-remove">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="p-0 border-r border-[var(--rule)] bg-white" style={{ minWidth: 108 }}>
                    <MoneyCell
                      value={Math.abs(Number(s.contractValue) || 0)}
                      onChange={(v) => update(i, { contractValue: toStored(v) })}
                      allowFlag={false}
                      testid="sub-contractvalue"
                    />
                  </td>
                  <td className="p-0 border-r border-[var(--rule)] bg-white" style={{ minWidth: W_STATUS }}>
                    <select
                      value={s.contract || "Yes"}
                      onChange={(e) => update(i, { contract: e.target.value })}
                      className="w-full px-2 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white"
                      title="Confirmed sub-consultant fees deduct from Backlog; Tentative ones deduct from Pipeline"
                      data-testid="sub-contract"
                    >
                      {contractValues.map((c) => (
                        <option key={c} value={c}>{c === "Yes" ? "Confirmed" : c}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-0 border-r border-[var(--rule)] bg-white" style={{ minWidth: W_NOTE }}>
                    <input
                      type="text"
                      value={s.note || ""}
                      onChange={(e) => update(i, { note: e.target.value })}
                      placeholder="who / discipline…"
                      className="w-full px-2 py-2 bg-transparent text-xs outline-none border-0 hover:bg-[var(--paper-soft)]/50 focus:bg-white placeholder:text-[var(--rule-strong)]"
                      data-testid="sub-note"
                    />
                  </td>
                  {months.map((mo) => (
                    <td key={mo} className={`p-0 border-r border-[var(--rule)]/40 ${isPast(mo) ? "bg-[var(--paper-soft)]/40" : "bg-white"}`} style={{ minWidth: 88 }}>
                      {lockPast(mo) ? (
                        <RatioCell
                          cv={-Math.abs(Number(s.contractValue) || 0)}
                          value={Number(s.revenue?.[mo]) || 0}
                          ratio={s.revenueRatios?.[mo] || null}
                          onChange={() => {}}
                          readOnly
                          pastMonth
                          testid={`sub-${mo}`}
                        />
                      ) : (
                        <RatioCell
                          cv={-Math.abs(Number(s.contractValue) || 0)}
                          value={Number(s.revenue?.[mo]) || 0}
                          ratio={s.revenueRatios?.[mo] || null}
                          onChange={(v, ratioStr) => updateMonthRatio(i, mo, v, ratioStr)}
                          negativeDefault
                          pastMonth={isPast(mo)}
                          testid={`sub-${mo}`}
                        />
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 border-l border-[var(--rule-strong)] bg-[var(--paper-soft)] sticky right-0 z-10" style={{ minWidth: 130, boxShadow: "-1px 0 0 var(--rule-strong)" }} data-testid="sub-total">
                    <div className="font-mono-num text-xs text-right">
                      {rowMag(s) ? `− ${fmtMoney(rowMag(s))}` : "—"}
                    </div>
                    <div
                      className={`text-[9px] leading-snug text-right ${allocColour(allocDecimal(s))}`}
                      data-testid="sub-allocated"
                      title={
                        allocDecimal(s) == null ? "Nothing spread yet"
                        : allocDecimal(s) > 1.005 ? "Over-spread — the months total more than the fee"
                        : allocDecimal(s) < 0.995 ? "Under-spread — the months don't yet fill the fee"
                        : "On target — the months total the fee exactly"
                      }
                    >
                      {allocLabel(allocDecimal(s))} · {fmtMoney(rowMag(s))} of {fmtMoney(Math.abs(Number(s.contractValue) || 0))}
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td className="sticky left-0 z-10 bg-[var(--paper-soft)] px-3 py-2 eyebrow border-r border-[var(--rule-strong)] border-t border-[var(--rule-strong)]" style={{ minWidth: W_LABEL }}>
                Sub-consultant total
              </td>
              <td className="px-2 py-2 font-mono-num text-xs text-right border-t border-[var(--rule-strong)] bg-[var(--paper-soft)]" data-testid="sub-fee-total">
                {cvTotal ? fmtMoney(cvTotal) : "—"}
              </td>
              <td className="border-t border-[var(--rule-strong)] bg-[var(--paper-soft)]" />
              <td className="border-t border-[var(--rule-strong)] bg-[var(--paper-soft)]" />
              {months.map((mo) => (
                <td key={mo} className="px-2 py-2 font-mono-num text-xs text-right border-t border-[var(--rule-strong)] border-r border-[var(--rule)]/40 bg-[var(--paper-soft)]" data-testid={`sub-monthly-total-${mo}`}>
                  {monthMag(mo) ? `− ${fmtMoney(monthMag(mo))}` : "—"}
                </td>
              ))}
              <td className="px-3 py-2 font-mono-num text-xs text-right border-t border-l border-[var(--rule-strong)] bg-[#2C4A38] text-white sticky right-0 z-10" style={{ minWidth: 130, boxShadow: "-1px 0 0 var(--rule-strong)" }} data-testid="sub-grand-total">
                − {fmtMoney(grand)}
              </td>
            </tr>
          </tbody>
        </table>
      </DragScrollContainer>
      <div className="border-t border-[var(--rule)] p-2 flex items-center justify-between flex-wrap gap-2">
        <button onClick={onAdd}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
          data-testid="add-subconsultants">
          <Plus size={11} /> Add sub consultant
        </button>
        <div className="text-[10px] font-mono-num text-[var(--soft-fg)] pr-2">
          Enter costs as positive figures — they are deducted from the forecast total.
          Month cells take a ratio of the fee (<span className="text-[var(--ink)]">5/10</span>),
          a decimal (<span className="text-[var(--ink)]">0.5</span>) or a dollar amount, or use the
          spread icon to split the fee evenly across the months in view.
          Status decides which bucket the deduction lands in — Confirmed reduces Backlog, Tentative reduces Pipeline.
        </div>
      </div>
    </div>
  );
}
